import * as routes from 'router/routes';
import { REQUEST_METHODS } from 'constants/api';
import { API_TAG_TYPES } from 'constants/apiTagTypes';
import { INVITATION_EMAIL_MISMATCH_ERROR } from 'constants/invitations';
import { ORGANISATIONS_STATUSES } from 'constants/organisations';
import { USER_PERMISSIONS } from 'constants/userPermissions';
import { NOTIFICATION_TYPES } from 'constants/notificationTypes';
import { downloadFile } from 'helper/downloadFile';
import { isInvitationEmailMismatch } from 'helper/isInvitationEmailMismatch';
import { addNotification } from 'helper/notifications';
import { baseApi } from 'store/baseApi';
import { logout } from 'store/entities/users/slice';

export const usersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCurrentUser: build.query({
      query: () => ({
        url: '/userprofile/users/current/',
      }),
      providesTags: [{ type: API_TAG_TYPES.CURRENT_USER }],
      transformResponse: (baseQueryReturnValue) => ({
        ...baseQueryReturnValue,
        user_permissions: baseQueryReturnValue.user_permissions.filter(
          (permission) => permission.organisation_status === ORGANISATIONS_STATUSES.ACTIVE,
        ),
      }),
    }),
    updateCurrentUserInfo: build.mutation({
      query: (data) => ({
        url: '/userprofile/users/current/',
        body: data,
        method: REQUEST_METHODS.PATCH,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.CURRENT_USER }],
    }),
    getOrganisationInvites: build.query({
      query: (params) => ({
        url: `/userprofile/invitations/`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.ORGANISATION_INVITES, id })),
              { type: API_TAG_TYPES.ORGANISATION_INVITES, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.ORGANISATION_INVITES, id: 'LIST' }],
      transformResponse: (invites) =>
        invites.map((invite) => ({
          ...invite,
          user_type: invite.is_organisation_admin ? USER_PERMISSIONS.ORGANISATION_ADMIN : USER_PERMISSIONS.USER,
        })),
    }),
    getFrameworkInvites: build.query({
      query: ({ frameworkId, params }) => ({
        url: `/userprofile/invitations/frameworks/${frameworkId}/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue) =>
        baseQueryReturnValue
          .map((invite) => ({
            ...invite,
            createdAt: new Date(invite.created_at).getTime(),
          }))
          .toSorted((a, b) => b.createdAt - a.createdAt),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.FEEDBACK_FRAMEWORK_INVITES, id })),
              { type: API_TAG_TYPES.FEEDBACK_FRAMEWORK_INVITES, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.FEEDBACK_FRAMEWORK_INVITES, id: 'LIST' }],
    }),
    createInvites: build.mutation({
      query: (data) => ({
        url: '/userprofile/invitations/',
        body: data,
        method: REQUEST_METHODS.POST,
      }),
      invalidatesTags: (result, error, { organisation }) =>
        result?.created_invitations?.length
          ? [
              ...(organisation
                ? [
                    { type: API_TAG_TYPES.ORGANISATION_INVITES, id: 'LIST' },
                    { type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' },
                    { type: API_TAG_TYPES.ACTION_OWNERS, id: 'LIST' },
                    { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
                  ]
                : [
                    { type: API_TAG_TYPES.SUPER_ADMIN_INVITES, id: 'LIST' },
                    { type: API_TAG_TYPES.SUPER_ADMINS, id: 'LIST' },
                  ]),
            ]
          : [],
    }),
    deleteInvites: build.mutation({
      query: (data) => ({
        url: '/userprofile/invitations/set-deleted-state/',
        body: data,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (result, error, { type }) => [
        { type, id: 'LIST' },
        ...(type === API_TAG_TYPES.ORGANISATION_INVITES
          ? [
              { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
              { type: API_TAG_TYPES.CULTURE_PROJECT_INVITES, id: 'LIST' },
              { type: API_TAG_TYPES.FEEDBACK_FRAMEWORK_INVITES, id: 'LIST' },
            ]
          : []),
        ...([API_TAG_TYPES.CULTURE_PROJECT_INVITES, API_TAG_TYPES.FEEDBACK_FRAMEWORK_INVITES].includes(type)
          ? [{ type: API_TAG_TYPES.ORGANISATION_INVITES, id: 'LIST' }, { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO }]
          : []),
      ],
    }),
    resendInvite: build.mutation({
      query: (data) => ({
        url: '/userprofile/invitations/resend/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;
          const successMessage =
            arg.ids.length === 1
              ? 'Invitation has been successfully sent.'
              : 'Invitations have been successfully sent.';

          addNotification(successMessage, NOTIFICATION_TYPES.SUCCESS);
        } catch (error) {
          console.error(error);
          addNotification('Invitations have not been sent.', NOTIFICATION_TYPES.ERROR);
        }
      },
    }),
    downloadFrameworkInvitesCsv: build.mutation({
      queryFn: async ({ frameworkId, params, fileName }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          url: `/userprofile/invitations/frameworks/${frameworkId}/download-csv/`,
          params,
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, fileName);

        return { data: null };
      },
    }),
    getProjectInvites: build.query({
      query: ({ projectId, params }) => ({
        url: `/userprofile/invitations/projects/${projectId}/`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.CULTURE_PROJECT_INVITES, id })),
              { type: API_TAG_TYPES.CULTURE_PROJECT_INVITES, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.CULTURE_PROJECT_INVITES, id: 'LIST' }],
      transformResponse: (invites) =>
        invites.length
          ? invites
              .map((invite) => ({
                ...invite,
                createdAt: new Date(invite.created_at).getTime(),
              }))
              .toSorted((a, b) => b.createdAt - a.createdAt)
          : [],
    }),
    getProjectInvitationsCsv: build.mutation({
      queryFn: async ({ projectId, params, fileName }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          url: `/userprofile/invitations/projects/${projectId}/download-csv/`,
          params,
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, fileName);

        return { data: null };
      },
    }),
    getSuperAdminInvites: build.query({
      query: (params) => ({
        url: '/userprofile/invitations/superuser-invites/',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.SUPER_ADMIN_INVITES, id })),
              { type: API_TAG_TYPES.SUPER_ADMIN_INVITES, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.SUPER_ADMIN_INVITES, id: 'LIST' }],
    }),
    getSuperAdminsList: build.query({
      query: (params) => ({
        url: '/userprofile/superusers/',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.SUPER_ADMINS, id })),
              { type: API_TAG_TYPES.SUPER_ADMINS, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.SUPER_ADMINS, id: 'LIST' }],
    }),
    deleteSuperAdmin: build.mutation({
      query: ({ superAdminId }) => ({
        url: `/userprofile/superusers/${superAdminId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.SUPER_ADMINS, id: 'LIST' }],
    }),
    resetUsersMfaDevices: build.mutation({
      query: ({ data, params }) => ({
        url: `/userprofile/mfa/unset-device/`,
        method: REQUEST_METHODS.POST,
        body: data,
        params,
      }),
      onQueryStarted: async ({ data }, api) => {
        try {
          await api.queryFulfilled;

          const successMessage =
            data.ids.length > 1
              ? "Users' devices have been successfully reset"
              : "User's device has been successfully reset";

          addNotification(successMessage, NOTIFICATION_TYPES.SUCCESS);
        } catch {
          // the failure is surfaced by the calling hook
        }
      },
    }),
    downloadSuperAdminsCsv: build.mutation({
      queryFn: async ({ fileName }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          url: '/userprofile/superusers/download-csv/',
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, fileName);

        return { data: null };
      },
    }),
    downloadInvitationsCsv: build.mutation({
      queryFn: async ({ params, fileName }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          params,
          url: '/userprofile/invitations/download-csv/',
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, fileName);

        return { data: null };
      },
    }),
    confirmUserInvitation: build.mutation({
      query: (data) => ({
        url: '/userprofile/grant-permission/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      onQueryStarted: async ({ invitation }, api) => {
        try {
          await api.queryFulfilled;
        } catch ({ error }) {
          if (isInvitationEmailMismatch(error)) {
            addNotification(INVITATION_EMAIL_MISMATCH_ERROR);
            api.dispatch(logout(`${routes.loginScreen}/${invitation}`));
          }
        }
      },
    }),
    changePassword: build.mutation({
      query: (data) => ({
        url: '/userprofile/change-password/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useUpdateCurrentUserInfoMutation,
  useGetOrganisationInvitesQuery,
  useGetFrameworkInvitesQuery,
  useDeleteInvitesMutation,
  useCreateInvitesMutation,
  useResendInviteMutation,
  useDownloadFrameworkInvitesCsvMutation,
  useGetProjectInvitesQuery,
  useGetProjectInvitationsCsvMutation,
  useGetSuperAdminInvitesQuery,
  useGetSuperAdminsListQuery,
  useDeleteSuperAdminMutation,
  useResetUsersMfaDevicesMutation,
  useDownloadSuperAdminsCsvMutation,
  useDownloadInvitationsCsvMutation,
  useConfirmUserInvitationMutation,
  useChangePasswordMutation,
} = usersApi;
